import React, { useContext, useRef, useState } from "react";
import { ImagePlus, Loader2 } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import { toast } from "sonner";
import { Button, Input, Label } from "@/components/ui";
import { ConfigContext } from "@/contexts/appContexts";
import { Blog } from "@/services/bedrock";
import { getValidFiles, saveElementToFirebase } from "@/utils";

type Props = {
  block: Blog.Block;
  readOnly: boolean;
  inputId: string;
  onChange: (patch: Partial<Blog.Block>) => void;
};

export const BlogMediaBlock: React.FC<Props> = ({ block, readOnly, inputId, onChange }) => {
  const { firebase } = useContext(ConfigContext);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const media = block.media;

  const uploadFile = async (file: File) => {
    setUploading(true);
    try {
      const uri = await saveElementToFirebase(firebase, "contentPages/blogs", file, uuidv4());
      const category = file.type.startsWith("video/") ? "VIDEO" : "IMAGE";
      onChange({
        media: {
          uri,
          category,
          type: file.type,
          extension: file.type.split("/").pop() || "",
        },
      });
    } catch (error) {
      console.error(error);
      toast.error("Could not upload that file");
    } finally {
      setUploading(false);
    }
  };

  const handleFilesSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = getValidFiles(Array.from(event.target.files || []))[0];
    if (file) void uploadFile(file);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        className="hidden"
        disabled={readOnly || uploading}
        onChange={handleFilesSelected}
      />

      {media?.uri ? (
        <div className="overflow-hidden rounded-lg border border-border bg-muted/30">
          {media.category === "VIDEO" ? (
            <video src={media.uri} controls className="max-h-64 w-full object-contain" />
          ) : (
            <img src={media.uri} alt={block.alt || ""} className="max-h-64 w-full object-contain" />
          )}
        </div>
      ) : (
        <button
          type="button"
          disabled={readOnly || uploading}
          onClick={() => fileInputRef.current?.click()}
          className="flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border px-4 py-8 text-sm text-muted-foreground hover:border-primary/50 hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
        >
          {uploading ? <Loader2 className="size-5 animate-spin" /> : <ImagePlus className="size-5" />}
          {uploading ? "Uploading…" : "Add an image or video"}
        </button>
      )}

      {media?.uri && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={readOnly || uploading}
            onClick={() => fileInputRef.current?.click()}
          >
            Replace file
          </Button>
        </div>
      )}

      <Label htmlFor={`${inputId}-alt`}>Caption</Label>
      <Input
        id={`${inputId}-alt`}
        value={block.alt ?? ""}
        disabled={readOnly}
        placeholder="Optional"
        onChange={event => onChange({ alt: event.target.value })}
      />
    </div>
  );
};
